import React, { useState, useRef, useEffect, useLayoutEffect, memo } from 'react';
import { classNames } from '../../utils/classNames';
import type { VirtualizerProps } from './Virtualizer.types';
import './Virtualizer.css';

const heightCache = new Map<string | number, number>();
const intersectionCounts = new Map<string | number, number>();
const intersectionResetTimers = new Map<string | number, number>();

const INTERSECTION_THROTTLE_THRESHOLD = 4; // After 5 rapid intersections, we temporarily ignore events.
const INTERSECTION_RESET_DELAY = 300; // Reset the count after 300ms of stability.

const VirtualizerComponent = ({
  children,
  placeholderHeight = 150,
  className,
  itemId
}: VirtualizerProps) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [measuredHeight, setMeasuredHeight] = useState<number>(
    (itemId !== undefined ? heightCache.get(itemId) : undefined) ??
      placeholderHeight
  );
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (itemId === undefined) return;

        // Clear any pending timer that would have reset the count.
        window.clearTimeout(intersectionResetTimers.get(itemId));

        const currentCount = intersectionCounts.get(itemId) ?? 0;
        const newCount = currentCount + 1;
        intersectionCounts.set(itemId, newCount);

        // If we've exceeded the threshold, ignore the intersection event to break the loop.
        if (newCount > INTERSECTION_THROTTLE_THRESHOLD) {
          // But, set a timer to reset the counter after a period of stability.
          const timerId = window.setTimeout(() => {
            intersectionCounts.delete(itemId);
          }, INTERSECTION_RESET_DELAY);
          intersectionResetTimers.set(itemId, timerId);
          return; // Ignore the event and exit.
        }

        // Set a timer that will reset the intersection count if no new intersections occur for this item.
        const timerId = window.setTimeout(() => {
          intersectionCounts.delete(itemId);
        }, INTERSECTION_RESET_DELAY);
        intersectionResetTimers.set(itemId, timerId);

        setIsIntersecting(entry.isIntersecting);
      },
      {
        rootMargin: '250px 0px 250px 0px',
        threshold: 0.01,
        trackVisibility: true,
        delay: 100
      } as any
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [itemId]);

  useLayoutEffect(() => {
    if (!shouldRender) return;
    const element = ref.current;
    if (!element) return;

    const measure = () => {
      const contentElement = element.firstElementChild;
      if (contentElement) {
        const height = contentElement.getBoundingClientRect().height;
        if (height > 0 && Math.abs(height - measuredHeight) > 0.5) {
          if (itemId !== undefined) {
            heightCache.set(itemId, height);
          }
          setMeasuredHeight(height);
        }
      }
    };

    measure();

    let cleanup: () => void = () => {};
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(element);
      cleanup = () => resizeObserver.disconnect();
    } else {
      const intervalId = window.setInterval(measure, 300);
      cleanup = () => window.clearInterval(intervalId);
    }

    return cleanup;
  }, [shouldRender, measuredHeight, children, itemId]);

  useEffect(() => {
    if (itemId === undefined || !heightCache.has(itemId)) {
      setMeasuredHeight(placeholderHeight);
    }
  }, [placeholderHeight, itemId]);

  useEffect(() => {
    if (isIntersecting) {
      setShouldRender(true);
      const fadeInTimer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(fadeInTimer);
    } else {
      setIsVisible(false);
      const unmountTimer = setTimeout(() => {
        setShouldRender(false);
      }, 220);
      return () => clearTimeout(unmountTimer);
    }
  }, [isIntersecting]);

  return (
    <div
      ref={ref}
      className={classNames(
        'virtualizer-wrapper',
        isVisible && 'virtualizer-wrapper--visible',
        className
      )}
      style={{ minHeight: measuredHeight }}
      virtualization-id={itemId}
    >
      {shouldRender ? children : null}
    </div>
  );
};

export const Virtualizer = memo(VirtualizerComponent);
